import type { MergedRewardData, RewardOverviewData } from '@/schemas/v2/reward'
import type { TFunction } from 'i18next'

type AcquisitionStatus = NonNullable<MergedRewardData['acquisitionStatus']>

/**
 * SELECT_ONE（groupType='SINGLE'）グループで「1つ分の選択を使い切った」ことを表すステータス。
 * バックエンドの SELECT_ONE ブロック判定（SelectOneGroupBlocking）と対応する集合で、
 * この状態のメンバーが1件でもあれば、残りが UNOBTAINED で返っていてもグループとしては終端。
 * 配列順がそのまま代表ステータスの優先順になる（達成系 → 完了系 → ハズレ）。
 *
 * LOST は非 repeatable のハズレ確定を想定している。repeatable + consumptionRequired の特典は
 * ハズレ後もスタンプを貯め直せば再挑戦できるが、その場合は再挑戦可能になった時点で
 * ACHIEVABLE が返り、上位の分岐が先に効くため一時的な表示に留まる（#3405）。
 */
const SINGLE_GROUP_OBTAINED_PRIORITY = [
  'ACHIEVED',
  'UNAPPLIED',
  'EXCHANGED',
  'APPLIED',
  'USED',
  'LOST',
] as const satisfies readonly AcquisitionStatus[]

/**
 * グループメンバーの獲得状況を代表値に集約する。
 * 優先順: ACHIEVABLE > (SINGLE のみ: 獲得済み/終端ステータス) > ACHIEVED > UNOBTAINED
 *         > (EXCHANGED/USED/UNAPPLIED/APPLIED/LOST) > ENDED/SOLD_OUT
 * 全メンバーが ENDED/SOLD_OUT の場合のみ終了系ステータスを返す。
 * （認証済みの場合のみ設定される。未認証時は全 null のため undefined を返す）
 */
function resolveGroupAcquisitionStatus(members: MergedRewardData[]): AcquisitionStatus | undefined {
  const statuses = members.map((m) => m.acquisitionStatus).filter((s): s is AcquisitionStatus => s != null)
  if (statuses.length === 0) return undefined
  // 「まだ獲得できる」は常に最優先。repeatable 特典は獲得後も ACHIEVABLE が返るため、
  // SINGLE の獲得済み判定より前に評価する必要がある。
  if (statuses.includes('ACHIEVABLE')) return 'ACHIEVABLE'
  // SELECT_ONE: 1件獲得（またはハズレ確定）すると残りは API 上 UNOBTAINED で返る（#2929 / #3405）ため、
  // UNOBTAINED より獲得済みメンバーのステータスを優先してグループ代表とする（#3405）。
  // MULTI は未獲得の特典が実際に残るため、従来どおり UNOBTAINED を優先する。
  if (members.some((m) => m.groupType === 'SINGLE')) {
    const obtained = SINGLE_GROUP_OBTAINED_PRIORITY.find((s) => statuses.includes(s))
    if (obtained !== undefined) return obtained
  }
  if (statuses.includes('ACHIEVED')) return 'ACHIEVED'
  if (statuses.includes('UNOBTAINED')) return 'UNOBTAINED'
  // ENDED/SOLD_OUT 以外のステータス（EXCHANGED/USED/UNAPPLIED/APPLIED/LOST）が 1 件でも含まれれば
  // 終了系にしない（全メンバーが ENDED/SOLD_OUT の場合のみ終了系を返す）
  const nonEndedStatuses = statuses.filter((s) => s !== 'ENDED' && s !== 'SOLD_OUT')
  if (nonEndedStatuses.length > 0) return nonEndedStatuses[0]
  return statuses[0]
}

/**
 * MergedRewardData[] を RewardOverviewData[] に変換する。
 * - groupType === 'NONE': そのまま個別エントリ
 * - groupType !== 'NONE': 同じ groupId を持つものを1エントリにまとめる
 *   - name: グループ名 (groupName)、null の場合は代表特典名にフォールバック
 *   - label: "N件の特典"
 *   - id / image: グループ先頭特典（APIレスポンス順に依存）
 *   - acquisitionStatus: resolveGroupAcquisitionStatus の結果
 */
export function buildDisplayRewards(t: TFunction, rewards: MergedRewardData[]): RewardOverviewData[] {
  const groupMembersMap = new Map<number, MergedRewardData[]>()
  for (const r of rewards) {
    if (r.groupType !== 'NONE' && r.groupId !== null) {
      const members = groupMembersMap.get(r.groupId) ?? []
      members.push(r)
      groupMembersMap.set(r.groupId, members)
    }
  }

  const result: RewardOverviewData[] = []
  const seenGroupIds = new Set<number>()

  for (const r of rewards) {
    if (r.groupType === 'NONE' || r.groupId === null) {
      result.push({
        id: r.id,
        name: r.name,
        image: r.image,
        label: r.label,
        acquisitionStatus: r.acquisitionStatus ?? undefined,
        conditions: r.conditions,
      })
    } else {
      if (!seenGroupIds.has(r.groupId)) {
        seenGroupIds.add(r.groupId)
        const groupMembers = groupMembersMap.get(r.groupId) ?? [r]
        result.push({
          id: r.id,
          name: r.groupName ?? r.name,
          image: r.image,
          label: t('reward.group.item_count', '{{count}}件の特典', { count: groupMembers.length }),
          acquisitionStatus: resolveGroupAcquisitionStatus(groupMembers),
          conditions: groupMembers[0]?.conditions ?? [],
        })
      }
    }
  }
  return result
}
